import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AppGlobals } from '@app/app.global';
import { NgxImageCompressService } from 'ngx-image-compress';
import * as pdfjsLib from 'pdfjs-dist';
import pdfWorker from 'pdfjs-dist/build/pdf.worker.entry';
import type { PDFDocumentProxy } from 'pdfjs-dist/types/src/display/api';

// Set worker source
(pdfjsLib as any).GlobalWorkerOptions.workerSrc = pdfWorker;

@Injectable({
  providedIn: 'root'
})
export class UploadService {

  constructor(private readonly http: HttpClient,
    private appGlobals: AppGlobals,
    private imageCompress: NgxImageCompressService) { }

  /**
   * Generate signed URL for the uploaded files and upload to S3 bucket
   * @param files 
   * @returns 
   */
  uploadFilesToS3(files: File[], entityId): Observable<any> {
    const fileData = {
      ...(entityId ? { entity: entityId } : {}),
      files: files.map(file => ({
        file: file.name,
        type: file.type
      }))
    };
    return new Observable(observer => {
      this.http.post<any>(`${this.appGlobals.apiUrl.invoice}presigned-url`, fileData)
        .subscribe(
          response => {
            const signedUrls = response;

            let uploadedCount = 0;
            const errors: string[] = [];

            const invoiceSources = [];
            files.forEach((file, index) => {
              if (file.name == signedUrls[index].name) {
                const signedUrl = signedUrls[index].path;
                const thumbnailUrl = signedUrls[index].thumbnail;

                // Upload the file using the signed URL
                this.uploadFileToS3(signedUrl, thumbnailUrl, file).subscribe(
                  () => {
                    const isPdf = file.type === 'application/pdf';
                    const buildSource = (pageCount: number) => {
                      const source: any = {
                        name: signedUrls[index].name,
                        path: signedUrls[index].sourcePath,
                        type: file.type,
                        size: file.size,
                        pageCount
                      };
                      if (!isPdf) {
                        source.thumbnail = signedUrls[index].sourceThumbnailPath;
                      }
                      invoiceSources.push(source);

                      uploadedCount++;
                      if (uploadedCount === files.length) {
                        // NOTE : Assuming that size is not same for 95% of the cases.
                        const nameOrder = files.map((item) => item.name);
                        const sizeOrder = files.map((item) => item.size);
                        invoiceSources.sort((a, b) => {
                          const indexA = nameOrder.indexOf(a.name);
                          const indexB = nameOrder.indexOf(b.name);
                          if (indexA === indexB) {
                            const sizeA = sizeOrder.indexOf(a.size);
                            const sizeB = sizeOrder.indexOf(b.size);
                            return sizeA - sizeB;
                          }
                          return indexA - indexB;
                        }); 
                        this.saveInvoiceMetaData(invoiceSources, entityId).subscribe(() => {
                          observer.next({ success: true, uploaded: uploadedCount });
                          observer.complete();
                        });
                      }
                    };
                    if (file.type === 'application/pdf') {
                      this.getPdfPageCount(file)
                        .then(buildSource)
                        .catch(err => {
                          buildSource(1); // Fallback to 1 page
                        });
                    } else {
                      buildSource(1); // Not a PDF
                    }

                  },
                  error => {
                    errors.push(`Error uploading file ${file.name}: ${error}`);
                    if (errors.length === files.length) {
                      observer.error(errors);  // If all uploads fail, send the errors
                    }
                  }
                );

              } else {
                console.log("File name not matched");
              }

            });

          },
          error => {
            observer.error(`Error getting signed URLs: ${error}`);
          }
        );
    });
  }



  /**
   * upload the file to S3 using the signed URL
   */
  private uploadFileToS3(signedUrl: string, thumbnailUrl: string, file: File): Observable<any> {
    return new Observable(observer => {
      fetch(signedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type
        },
        body: file
      })
        .then(response => {
          if (response.ok) {
            if (file.type.startsWith('image/')) {
              this.compressFile(thumbnailUrl, file);
            }
            observer.next(response);
            observer.complete();
          } else {
            observer.error(response);
          }
        })
        .catch(error => {
          observer.error(error);
        });
    });
  }

  // private uploadFileToS3(signedUrl: string, thumbnailUrl: string, file: File): Observable<number | any> {
  //   return new Observable(observer => {
  //     const xhr = new XMLHttpRequest();
  //     xhr.open('PUT', signedUrl);
  //     xhr.setRequestHeader('Content-Type', file.type);

  //     // Emit progress as percentage
  //     xhr.upload.onprogress = (event) => {
  //       if (event.lengthComputable) {
  //         const percentComplete = Math.round((event.loaded / event.total) * 100);
  //         observer.next(percentComplete); // emit progress
  //       }
  //     };

  //     xhr.onload = () => {
  //       if (xhr.status === 200) {
  //         if (file.type.startsWith('image/')) {
  //           this.compressFile(thumbnailUrl, file);
  //         }
  //         observer.next({ status: 'complete', file });  // <<< emit a complete object
  //         observer.complete();
  //       } else {
  //         observer.error(xhr.statusText || `Upload failed with status ${xhr.status}`);
  //       }
  //     };

  //     xhr.onerror = () => {
  //       observer.error('Upload error occurred.');
  //     };

  //     xhr.send(file);
  //   });
  // }


  /**
   * compress image for thumbnail
   * @param file 
   */
  compressFile(thumbnailUrl, file) {
    const reader = new FileReader();
    reader.onload = (event: any) => {
      const image = event.target.result;
      this.imageCompress.compressFile(image, -1, 10, 10).then(
        result => {
          const blob = this.base64ToBlob(result, file.type);
          fetch(thumbnailUrl, {
            method: 'PUT',
            headers: {
              'Content-Type': file.type
            },
            body: blob
          })
            .then(response => {
            })
        }
      );
    };
    reader.readAsDataURL(file);
  }

  /**
   * base64 to blob
   * @param base64 
   * @param mime 
   * @returns 
   */
  base64ToBlob(base64: string, mime: string): Blob {
    const byteString = atob(base64.split(',')[1]);
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const intArray = new Uint8Array(arrayBuffer);
    for (let i = 0; i < byteString.length; i++) {
      intArray[i] = byteString.charCodeAt(i);
    }
    return new Blob([intArray], { type: mime });
  }

  /**
   * get pdf page counter
   * @param file 
   * @returns 
   */
  getPdfPageCount(file: File): Promise<number> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        const typedArray = new Uint8Array(reader.result as ArrayBuffer);
        pdfjsLib.getDocument({ data: typedArray }).promise
          .then((pdf: PDFDocumentProxy) => resolve(pdf.numPages))
          .catch(err => reject(err));
      };

      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }


  /**
   * save invoice metadata
   */
  saveInvoiceMetaData(sources, entityId): Observable<any> {
    const InvoiceMetaData = {
      sources,
      ...(entityId ? { entity: entityId } : {})
    };

    return this.http.post<any>(
      `${this.appGlobals.apiUrl.invoice}save-invoice-metadata`,
      InvoiceMetaData
    );
  }

}
